import * as React from 'react';
import { useLocation, useHistory, useParams } from 'react-router-dom';

import once from 'lodash/once';

import { BaseParams } from '@common/typescript/objects/BaseParams';
import {
	addPrefix,
	getKeysByPrefix,
} from '@common/react/utils/ObjectKeysPrefix/objectKeysPrefix';
import {generateGUID} from '@common/react/utils/utils';

import { List } from '@common/typescript/objects/List';
import { WithDeleted } from '@common/typescript/objects/WithDeleted';
import { handleUrl, getSearchParamsFromUrl } from '@common/react/utils/FIltersParamsFromUrl/FiltersParamsFromUrl';
import useRequest from '@common/react/hooks/useRequest';

export enum Mode {
	View,
	Edit
}

export enum SortingDirection {
	Default = 0,
	Ascending = 1,
	Descending = 2
}

export interface WithKey extends WithDeleted {
	keyGUID?: string;
}

interface Edits<T extends WithKey> {
	[key: string]: T;
}

interface FiltersRef {
	filters: BaseParams;
	silent?: boolean;
	concatResult?: boolean;
	resetFilters?: boolean;
}

export interface ItemsProviderProps<T extends WithKey> {
	type: string;
	children: any;
	items?: T[];
	filters?: BaseParams;
	loadRequest?: string;
	saveRequest?: string;
	saveAllRequest?: string;
	pagination?: any;
	add?: (items: T[]) => T;
	multiple?: boolean;
	validationSchema?: any;
	clearForSubmit?: (item: T) => any;
	onRequestError?: ((error: string) => void);
	onValidationError?: ((key, err, error: any) => void);
	mode?: Mode;
	objectKey?: string;
	arrayName?: string;
	updateItems?: (items: T[]) => void;
	onLoad?: (res: any) => void;
	transformItems?: (res: any, filters: any) => any;
	unhandledFilters?: any;
	withHandleUrl?: boolean;
	transformFiltersBeforeHandleUrl?: (filters) => any;
	path?: string;
	getPageFromParams?: (params) => any;
	filtersPrefix?: string;
	pageInSearch?: boolean;
	addedFirst?: boolean;
	usePropsPagination?: boolean;
	defaultFilters?: BaseParams;
	afterResetFilters?: (filters?: BaseParams) => void; // for clear autocompletes or other filters
	editItems?: Edits<T>;
	syncItems?: T[];
	delay?: number; // ms
	onItemsChange?: (items: T[], filters?: any) => void;
	skipValidationAll?: boolean;
}

export interface ItemsProviderContextState<T extends WithKey> {
	items: T[];
	loading: boolean;
	pagination: any;
	filters: any;
	edits: any;
	errors: any;
	loaders: any;
	multiple: boolean;
	type: string;
	selectedRows: T[];
	initialValues?: any;
	addedFirst?: boolean;
	advancedItems?: T[]; // used in AdvancedItemsProvider
}

interface LoadData {
	page: number;
	pageSize: number;
	showSizeChanger?: boolean;
	pageSizeOptions?: Array<number | string>;
	offset?: number;
}

export interface ItemsProviderContext<T extends WithKey> {
	state: ItemsProviderContextState<T>;
	actions: any;
}

export const createItemsProviderContext = once(<T extends WithKey, >() => React.createContext({} as ItemsProviderContext<T>));

export const useItemsProviderContext: <T extends WithKey>() => ItemsProviderContext<T> = <T extends WithKey, >() =>
	React.useContext(createItemsProviderContext<T>());

export const defaultPageSizeOptions = [10, 20, 30, 40];

const addGUID = (items: Array<WithKey>) => items.map(item => ({...item, keyGUID: generateGUID()}));

export const defaultTransformFiltersBeforeHandleUrl = filters => ({
	...filters,
	page: undefined,
	count: filters.count || filters.pageSize,
	current: undefined,
	showSizeChanger: undefined,
	total: undefined,
	offset: undefined,
	pageSizeOptions: undefined,
	pageSize: undefined
});

export const ItemsProvider: <T extends WithKey>(p: ItemsProviderProps<T>) => React.ReactElement<T> = <T extends WithKey, >({
	items = [] as any,
	type,
	filters = {},
	loadRequest = type.includes('Remote') ? `${type.replace('Remote', '')}ListRemote` : `${type}List`,
	saveRequest = type,
	saveAllRequest = '',
	children,
	pagination = { current: 1, pageSize: 10, showSizeChanger: false, total: 0 } as any,
	add = items => ({id: -1}),
	multiple = false,
	validationSchema  = undefined as any,
	clearForSubmit = item => item,
	onRequestError = (error: string) => {},
	onValidationError = (key, err, error: any) => {},
	mode = Mode.View,
	objectKey = '',
	arrayName = '',
	updateItems = (items) => {},
	onLoad = (res) => {},
	transformItems = (res, filters) => res,
	unhandledFilters = {},
	transformFiltersBeforeHandleUrl = defaultTransformFiltersBeforeHandleUrl,
	withHandleUrl = false,
	getPageFromParams = ({ page }) => page,
	filtersPrefix= '',
	path = '',
	pageInSearch = false,
	defaultFilters = {} as BaseParams,
	afterResetFilters = () => {},
	addedFirst = false,
	usePropsPagination = false, // for AdvancedItemsProvider,
	editItems = undefined as any,
	syncItems = undefined as any,
	delay = 0,
	onItemsChange = undefined as any,
	skipValidationAll = false
}) => {
	const ItemsContext = createItemsProviderContext<T>();
	const location = useLocation();
	const history = useHistory();
	const params = useParams<any>();

	const [edits, setEdits] = React.useState<Edits<T>>(editItems || {});
	const [_items, _setItems] = React.useState<T[]>(items ? addGUID(items) as T[] : []);
	const [loaders, setLoaders] = React.useState<any>({});
	const [loading, setLoading] = React.useState(false);
	const [_filters, setFilters] = React.useState<BaseParams>({
		...filters,
		pageSize: pagination.pageSize,
		current: pagination.current,
		...(withHandleUrl ? getKeysByPrefix(getSearchParamsFromUrl(location, filtersPrefix), filtersPrefix) : {}),
		...(usePropsPagination ? {count: undefined} : {})
	});
	const [_pagination, setPagination] = React.useState(withHandleUrl && !usePropsPagination
		? {
			...pagination,
			current: pageInSearch ? _filters.page || pagination.current : pagination.current,
			pageSize: _filters.count || _filters.pageSize || pagination.pageSize,
			showSizeChanger: !!pagination.showSizeChanger
		}
		: {
			...pagination,
			showSizeChanger: !!pagination.showSizeChanger
		}
	);
	const [errors, setErrors] = React.useState<any>({});
	const [id, setId] = React.useState<number>(-1);
	const [selectedRows, setSelectedRows] = React.useState<T[]>([]);
	// const [refs, setRef] = React.useState<any>([]);
	const filtersRef = React.useRef<FiltersRef | null>(null);
	const pageSizeOptions: Array<string | number> = pagination.pageSizeOptions || defaultPageSizeOptions;
	const request = useRequest();

	let refs = {};
	const setRef = (obj) => {
		refs = { ...refs, ...obj };
	};

	const initEdits = (items, edits) => {
		let _edits = edits;

		if (mode === Mode.Edit) {
			const temp = {};

			for (let i = 0; i < items.length; i++) {
				const edit = edits[items[i].id];

				if (!edit) {
					if (items[i].id < 0) {
						setId(id - 1);
						items[i].id = id;
					}

					temp[items[i].id] = items[i];
				} else {
					temp[items[i].id] = { ...edit, ...items[i] };
				}
			}

			_edits = { ..._edits, ...temp };
		} else {
			_edits = {};
		}

		return _edits;
	};

	React.useEffect(() => {
		if (!!syncItems) {
			_setItems(syncItems);
			setEdits(initEdits(items, edits));
		}
	}, [syncItems]); // syncItems should be memoized or kept in state to avoid endless re-renders

	React.useEffect(() => {
		setEdits(initEdits(!_items.length ? items : _items, editItems || edits));
	}, [mode, Object.keys(editItems || {}).sort().toString()]);

	React.useEffect(() => {
		if (withHandleUrl) {
			const transformedFilters = transformFiltersBeforeHandleUrl(_filters);
			const filters = filtersPrefix ? addPrefix(transformedFilters, filtersPrefix) : transformedFilters;

			handleUrl(filters, location, history, _filters.current, filtersPrefix, pageInSearch);
		}
	}, [_filters]);

	React.useEffect(() => {
		if (withHandleUrl && path && !params.page && location.search) {
			history.replace({
				...location,
				pathname: `/${path.match(/\/d+$/) ? path : `${path}/${_filters.current || ''}`}`,
			});
		}
	}, [_filters.current, location.search]);

	React.useEffect(() => {
		!_items.length && _setItems(items);
		setEdits(initEdits(!_items.length ? items : _items, edits));
	}, []);

	const setItems = (items) => {
		_setItems(items);
		onItemsChange && onItemsChange(items, _filters);

		setEdits(initEdits(items, edits));
	};

	const resetItems = (items, filters) => {
		const transformedItems = transformItems(items, filters);

		_setItems(transformedItems);
		onItemsChange && onItemsChange(transformedItems, filters);

		setEdits(initEdits(transformedItems, {}));
	};

	// "silent" used for load without showing loading

	const load = (
		data: LoadData = { page: 1, pageSize: 10, showSizeChanger: false },
		silent?: boolean,
		concatResult?: boolean,
		resetFilters?: boolean,
		reverseResult?: boolean
	) => {
		!silent && setLoading(true);
		const current = data.page || 1;

		const pageSize = pageSizeOptions.find(s => +data.pageSize === +s) ?
			_pagination.showSizeChanger ? (data.pageSize || _pagination.pageSize) : _pagination.pageSize
			: _pagination.pageSize || (pageSizeOptions.length > 0 ? pageSizeOptions[0] : 10);
		const newPageSize = pageSizeOptions.find(s => +pageSize === +s) ? pageSize : pageSize - 1;

		let newFilters: BaseParams = {..._filters};
		resetFilters && Object.keys(_filters).forEach(key => newFilters[key] = undefined);
		newFilters = {
			...newFilters,
			...(resetFilters ? {...unhandledFilters, ...defaultFilters} : {}),
			...data,
			showSizeChanger: undefined,
			count: _filters.count ? pageSize : undefined
		};

		const offset = concatResult ? _items?.length : data.offset || (pageSize !== _pagination.pageSize ? (current - 1) * newPageSize : 0);

		setFilters(newFilters);
		resetFilters && afterResetFilters && afterResetFilters();

		return request<List<T>>(loadRequest, {
			...unhandledFilters,
			...newFilters,
			page: data.offset ? undefined : current,
			current: data.offset ? undefined : newFilters.current,
			offset: offset,
			count: pageSize,
		}).then((res) => {
			!silent && setLoading(false);

			const resultItems = concatResult ?
				reverseResult ? res.list.reverse().concat(_items) : _items.concat(res.list)
					: reverseResult ? res.list.slice(0, newPageSize).reverse() : res.list.slice(0, newPageSize);

			resetItems(addGUID(resultItems), newFilters);

			setPagination(prev => ({
				...prev,
				current: current,
				pageSize: newPageSize,
				total: res.count
			}));
			setSelectedRows(silent ? selectedRows.filter((item: T) => res.list.find(({id}) => item.id === id)) : []);

			onLoad && onLoad(res);
			return res;
		}).catch((error) => {
			!silent && setLoading(false);

			onRequestError(error);

			throw error;
		});
	};

	const loadDelay = (
		data: LoadData = { page: 1, pageSize: 10, showSizeChanger: false },
		silent?: boolean,
		concatResult?: boolean,
		resetFilters?: boolean,
		reverseResult?: boolean
	) => {
		if (delay) {
			filtersRef.current = {
				filters: {...filtersRef.current?.filters, ...data},
				silent,
				concatResult,
				resetFilters
			};
			setFilters(prev => ({
				...prev,
				...filtersRef.current?.filters,
				showSizeChanger: undefined,
				count: prev.count,
				pageSize: prev.pageSize,
			}));

			return new Promise(resolve => setTimeout(resolve, delay))
				.then(() => {
					if (filtersRef.current !== null) {
						const {filters, silent, concatResult, resetFilters} = filtersRef.current;
						filtersRef.current = null;
						return load(filters as any, silent, concatResult, resetFilters, reverseResult);
					}
				});
		}

		return load(data, silent, concatResult, resetFilters, reverseResult);
	};

	const loadMore = (data: LoadData, reverseResult?: boolean, silent?: boolean) => loadDelay(data, silent, true, false, reverseResult);

	const handleChange = (params) => {
		if (params?.currentTarget) {
			const { name, value } = params.currentTarget;
			return loadDelay({[name]: value} as any);
		}
		return loadDelay(params);
	};

	const saveItem = (record, requestName = saveRequest) => {
		if (!requestName) {
			throw 'Need saveRequest';
		}

		const item = { ...record, ...clearForSubmit(record) };

		if (refs[item.id]) {
			/*
			const r = refs[item.id](item);

			item = r.item;

			promises.push(r.validate);
			*/
		}

		setLoaders({ ...loaders, [record.id]: true });

		return request(requestName, item).then((response: any) => {
			setLoaders({ ...loaders, [record.id]: undefined });

			return response;
		}).catch((error: string) => {
			setLoaders({ ...loaders, [record.id]: undefined });

			onRequestError(error);

			throw error;
		}).finally(() => setPagination(prev => ({
			...prev,
			..._pagination,
			pageSize: pageSizeOptions.find(s => +_pagination.pageSize === +s) ?
				_pagination.pageSize : _pagination.pageSize - 1
		})));
	};

	const save = (record, skipValidation?: boolean, saveRequest?: string) => {
		const modifiedRecord = {...record, ...(record.deleted ? record : edits[record.id])};

		if (!record.deleted && validationSchema && !skipValidation) {
			return validationSchema?.validate(modifiedRecord, { abortEarly: false }).then(() => {
				return saveItem(modifiedRecord, saveRequest);
			}).catch((err) => {
				if (err.inner) {
					const er = {};
					for (let i = 0; i < err.inner.length; i++) {
						er[err.inner[i].path] = err.inner[i].errors[0];
					}

					setErrors({ ...errors, [record.id]: er });

					onValidationError(record.id, er, err);
				} else {
					throw err;
				}
			});
		}

		return saveItem(modifiedRecord, saveRequest);
	};

	const reload = (p, resetFilters?: boolean) => {
		let page = p?.current || _pagination.current;
		const pageSize = p?.pageSize || _pagination.pageSize;
		const pageSizeIsInclude = !!pageSizeOptions.find(s => +pageSize === +s);

		if (!pageSizeIsInclude && !addedFirst) {
			page = Math.floor(_pagination.total / (_pagination.pageSize - 1) + 1);
		}

		return loadDelay({
			...p,
			page: resetFilters ? 1 : page,
			pageSize: pageSizeIsInclude ? pageSize : pageSize - 1,
			showSizeChanger: (p || _pagination).showSizeChanger
		}, false, false, resetFilters);
	};

	const update = (items: T[]) => {
		setItems(items);
		if (!pageSizeOptions.find(s => +_pagination.pageSize === +s)) {
			setPagination(prev => ({
				...prev,
				..._pagination,
				pageSize: _pagination.pageSize - 1
			}));
		}
	};

	// const _add = () => add();

	const saveItems = (items, reloadHandler = reload) => {
		if (saveAllRequest === '') throw 'save all request is not available';

		setLoading(true);

		return request<List<T>>(saveAllRequest, items)
			.then(() => {
				setEdits({});

				reloadHandler(_pagination)
					.catch(onRequestError)
					.finally(() => {
						setLoading(false);
					});
			})
			.catch((error: string) => {
				setLoading(false);
				onRequestError(error);
				throw error;
			});
	};

	const deleteItems = items => saveItems(items.map(value => ({ id: value.id, deleted: true})));

	const validateAll = (skipValidation) => {
		const items: T[] = [];

		if (validationSchema && !skipValidation) {
			setErrors({});

			const promises: any[] = [];

			for (const key in edits) {
				const item = edits[key];

				items.push({ ...item, ...clearForSubmit(item) });

				promises.push(validationSchema?.validate(item, { abortEarly: false })
					.catch((err) => {
						if (err.inner) {
							const er = {};
							for (let i = 0; i < err.inner.length; i++) {
								er[err.inner[i].path] = err.inner[i].errors[0];
							}

							setErrors(prev => ({ ...prev, [key]: er }));

							onValidationError(key, er, err);
						} else {
							throw err;
						}
					})
				);
			}

			if (promises.length > 0) {
				return Promise.all(promises).then((results) => {
					if (!results.some(q => typeof q === 'undefined')) {
						return Promise.resolve(items);
					}

					// console.log(errors);
				});
			}
		} else {
			for (const key in edits) {
				items.push({ ...edits[key], ...clearForSubmit(edits[key]) });
			}
		}

		return Promise.resolve(items);
	};

	const saveAll = (skipValidation) => {
		if (saveAllRequest === '') throw 'save all request is not available';

		if (Object.keys(edits).length <= 0) throw 'There are no edited items';

		const items: T[] = [];

		if (validationSchema && !skipValidation) {
			setErrors({});

			const promises: any[] = [];

			for (const key in edits) {
				let item = edits[key];

				if (refs[item.id]) {
					const r = refs[item.id](item);

					item = r.item;

					promises.push(r.validate);
				}

				items.push({ ...item, ...clearForSubmit(item) });

				promises.push(validationSchema?.validate(item, { abortEarly: false })
					.catch((err) => {
						if (err.inner) {
							const er = {};
							for (let i = 0; i < err.inner.length; i++) {
								er[err.inner[i].path] = err.inner[i].errors[0];
							}

							setErrors(prev => ({ ...prev, [key]: er }));

							onValidationError(key, er, err);
						} else {
							throw err;
						}
					})
				);
			}

			if (promises.length > 0) {
				Promise.all(promises).then((results) => {
					if (!results.some(q => typeof q === 'undefined')) {
						return saveItems(items);
					}
				});
			}
		} else {
			for (const key in edits) {
				items.push({ ...edits[key], ...clearForSubmit(edits[key]) });
			}

			return saveItems(items);
		}
	};

	const setEdit = (record) => {
		setEdits({ ...edits, [record.id]: record });
	};

	const addItem = (item?: any) => {
		setId(id - 1);

		const newItem = item ? { ...item, id: id } : { ...add(_items), id: id };

		// if (_items.length === _pagination.pageSize) {
		// 	setPagination(prev => ({...prev, ..._pagination, pageSize: _pagination.pageSize + 1 }));
		// }

		update(addedFirst ? [newItem].concat(_items) : _items.concat(newItem));

		updateItems && updateItems(addedFirst ? [newItem].concat(_items) : _items.concat(newItem));

		// setItems(_items.concat(newItem));

		setEdit(newItem);
	};

	const addRef = (key, f) => {
		setRef({ [key]: f });
	};

	const value = {
		state: {
			items: _items,
			loading: loading,
			pagination: _pagination,
			filters: _filters,
			edits: edits,
			errors: errors,
			loaders: loaders,
			multiple: multiple,
			type: type,
			selectedRows,
			addedFirst,
			saveRequest
		},
		actions: {
			load: loadDelay,
			save: save,
			saveAll: saveAll,
			update: update,
			add: addItem,
			setEdits: setEdits,
			setErrors: setErrors,
			reload: reload,
			setEdit: setEdit,
			setItems: setItems,
			addRef: addRef,
			setSelectedRows,
			deleteItems,
			handleChange,
			loadMore,
			saveItems,
			setLoading,
			validateAll
		}
	};

	const context = useItemsProviderContext<any>();

	if (context.state) {
		// ItemsProvider in ItemsProvider

		React.useEffect(() => {
			objectKey && arrayName && context.actions.addRef(objectKey, (item) => {
				const items: T[] = [];

				for (const key in edits) {
					items.push({ ...edits[key], ...clearForSubmit(edits[key]) });
				}

				return { item: { ...item, [arrayName]: items }, validate: validateAll(false) };
			});
		}, [edits]);
	} else {
		React.useEffect(() => {
			!skipValidationAll && validateAll(false);
		}, [edits]);
	}

	return (
		<ItemsContext.Provider value={value}>
			{children}
		</ItemsContext.Provider>
	);
};